import { handleActions } from 'redux-actions';
import * as ScenarioActions from 'app/actions/scenarios/scenarioAction.ts';
import type { LinkedScenario, ICurrentScenario } from '../../models/intents/intentNodeProperties.ts';
import type { UserSaysPhrases } from 'app/models/scenarios/scenario.ts';
import type { ScenarioDto } from '@/api-gen/api.schemas';

export interface IScenarioState {
  scenarios: Array<ScenarioDto>;
  scenariosLoading: boolean;
  isDetailedScenarioUpdate: boolean;
  detailedScenarioLoading: boolean;
  detailedScenarioSaving: boolean;
  detailedScenarioIsOpening: boolean;
  scenarioHasNotBeenModified: boolean;
  parentScenario?: Array<LinkedScenario>;
  rootScenario?: ScenarioDto;
  currentScenario?: ICurrentScenario;
  linkedScenarios: Array<LinkedScenario>;
  toNextlinkedScenario: boolean;
  userSaysPhrasesByScenarioId?: UserSaysPhrases;
}

const initialState: IScenarioState = {
  scenarios: new Array<ScenarioDto>(),
  scenariosLoading: false,
  detailedScenarioLoading: false,
  detailedScenarioSaving: false,
  detailedScenarioIsOpening: false,
  scenarioHasNotBeenModified: true,
  isDetailedScenarioUpdate: true,
  parentScenario: undefined,
  rootScenario: undefined,
  currentScenario: undefined,
  linkedScenarios: new Array<LinkedScenario>(),
  toNextlinkedScenario: false,
  userSaysPhrasesByScenarioId: undefined
};

export const scenarioReducer = handleActions<
  IScenarioState,
  Array<ScenarioDto> | ScenarioDto | boolean | LinkedScenario | Array<LinkedScenario> | number | UserSaysPhrases
>(
  {
    [ScenarioActions.Type.SET_SCENARIOS]: (state, action): IScenarioState => {
      return { ...state, scenarios: action.payload as Array<ScenarioDto> };
    },
    [ScenarioActions.Type.SET_IS_DETAILED_SCENARIO_UPDATE]: (state, action): IScenarioState => {
      return { ...state, isDetailedScenarioUpdate: action.payload as boolean };
    },
    [ScenarioActions.Type.SET_DETAILED_SCENARIO_DATA_BY_SCENARIO_ID]: (state, action): IScenarioState => {
      return { ...state, userSaysPhrasesByScenarioId: action.payload as UserSaysPhrases };
    },
    [ScenarioActions.Type.SET_SCENARIO_HAS_NOT_BEEN_MODIFIED]: (state, action): IScenarioState => {
      return { ...state, scenarioHasNotBeenModified: action.payload as boolean };
    },
    [ScenarioActions.Type.SET_SCENARIOS_LOADING]: (state, action): IScenarioState => {
      return { ...state, scenariosLoading: action.payload as boolean };
    },
    [ScenarioActions.Type.SET_DETAILED_SCENARIO_LOADING]: (state, action): IScenarioState => {
      return { ...state, detailedScenarioLoading: action.payload as boolean };
    },
    [ScenarioActions.Type.SET_DETAILED_SCENARIO_SAVING]: (state, action): IScenarioState => {
      return { ...state, detailedScenarioSaving: action.payload as boolean };
    },
    [ScenarioActions.Type.SET_DETAILED_SCENARIO_IS_OPENING]: (state, action): IScenarioState => {
      return { ...state, detailedScenarioIsOpening: action.payload as boolean };
    },
    [ScenarioActions.Type.SET_ROOT_SCENARIO]: (state, action): IScenarioState => {
      return { ...state, rootScenario: action.payload as ScenarioDto };
    },
    [ScenarioActions.Type.SET_PARENT_SCENARIO]: (state, action): IScenarioState => {
      return { ...state, parentScenario: action.payload as Array<LinkedScenario> };
    },
    [ScenarioActions.Type.SET_CURRENT_SCENARIO]: (state, action): IScenarioState => {
      return { ...state, currentScenario: action.payload as ICurrentScenario };
    },
    [ScenarioActions.Type.SET_CURRENT_SCENARIO_SAVE_STATUS]: (state, action): IScenarioState => {
      return {
        ...state,
        currentScenario: state.currentScenario && {
          ...state.currentScenario,
          isScenarioSaved: action.payload as boolean
        }
      };
    },
    [ScenarioActions.Type.SET_LINKED_SCENARIOS]: (state, action): IScenarioState => {
      return { ...state, linkedScenarios: action.payload as Array<LinkedScenario> };
    },
    [ScenarioActions.Type.TO_NEXT_LINKED_SCENARIO]: (state, action): IScenarioState => {
      return { ...state, toNextlinkedScenario: action.payload as boolean };
    },
    [ScenarioActions.Type.UPDATE_SCENARIO_VARIABLES_PARTIAL]: (state, action): IScenarioState => {
      // Type assertion to fix TypeScript error
      const payload = action.payload as unknown as { scenarioId: number; variables: any[] };
      const { scenarioId, variables } = payload;

      const updatedScenarios = state.scenarios.map((scenario) => {
        if (scenario.id === scenarioId) {
          return { ...scenario, variables };
        }
        return scenario;
      });

      return { ...state, scenarios: updatedScenarios };
    },
    [ScenarioActions.Type.UPDATE_SCENARIO_PARTIAL]: (state, action): IScenarioState => {
      // Type assertion to fix TypeScript error
      const payload = action.payload as unknown as { scenarioId: number; updates: Partial<ScenarioDto> };
      const { scenarioId, updates } = payload;

      const updatedScenarios = state.scenarios.map((scenario) => {
        if (scenario.id === scenarioId) {
          return { ...scenario, ...updates };
        }
        return scenario;
      });

      return { ...state, scenarios: updatedScenarios };
    }
  },
  initialState
);
